<script setup lang="ts">
import NotificationContainer from './components/notification/NotificationContainer.vue'
import AlertDialog from './components/dialogs/AlertDialog.vue'
import LoadingSpinner from './components/base/LoadingSpinner.vue'
import { usePersonalAccountDataState } from './utilities/account/useAccountDataPersonal'
import { useWalletState, WalletState } from './utilities/account/useWallet'
import WalletTransactions from './components/account/wallet/WalletTransactions.vue'
import WalletHeader from './components/account/wallet/WalletHeader.vue'
import AccountSidePanel from './components/account/AccountSidePanel.vue'
import WalletPendingWithdrawals from './components/account/wallet/WalletPendingWithdrawals.vue'
import InlineAlert from './components/base/InlineAlert.vue'
import { isLegalProfile, isNaturalProfile } from './utilities/investment-process/useUserProfileType'
import { useLegalAccountDataState } from './utilities/account/useAccountDataLegal'
import { AccountDataState } from './typings'
import { onMounted } from 'vue'
import { getAdminEmail } from './utilities/usePlatformSettings'
import BaseButtonMuted from './components/base/BaseButtonMuted.vue'
import WalletIcon from './components/base/icons/WalletIcon.vue'
import VoucherOverview from './components/account/wallet/VoucherOverview.vue'

const walletState = useWalletState() as WalletState

//@ts-ignore
const accountState: AccountDataState = isLegalProfile() ? useLegalAccountDataState() : usePersonalAccountDataState()

onMounted(async () => {
    await accountState.fetchAccountData()
    await walletState.fetchWalletData()
})

const urlParams = new URLSearchParams(window.location.search)
const paymentStatus = urlParams.get('payment_status')
</script>

<template>
    <div class="bg-gray-50">
        <NotificationContainer />
        <AlertDialog />
        <main v-if="accountState.loaded && !walletState.loading" class="mb-8 mt-5 space-y-8">
            <!-- Page header -->
            <div class="max-w-3xl md:flex md:items-center md:justify-between md:space-x-5">
                <div class="flex items-center space-x-5">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900">
                            {{ isLegalProfile() ? $t('wallet.legal_wallet') : $t('wallet.my_wallet') }}
                        </h1>
                        <p class="text-sm font-medium text-gray-500">
                            {{
                                isLegalProfile() ? $t('wallet.legal_wallet_subtitle') : $t('wallet.my_wallet_subtitle')
                            }}
                        </p>
                    </div>
                </div>
            </div>

            <section aria-labelledby="payment-messages" class="space-y-4">
                <InlineAlert
                    v-if="paymentStatus === 'success'"
                    type="Success"
                    :title="$t('wallet.dialogs.money_deposit_successful_title')"
                    :description="$t('wallet.dialogs.money_deposit_successful_description')" />
                <InlineAlert
                    v-else-if="paymentStatus === 'canceled'"
                    type="Warning"
                    :title="$t('wallet.dialogs.money_deposit_canceled_title')"
                    :description="$t('wallet.dialogs.money_deposit_canceled_description')" />
                <InlineAlert
                    v-else-if="paymentStatus === 'error'"
                    type="Error"
                    :title="$t('wallet.dialogs.money_deposit_error_title')"
                    :description="$t('wallet.dialogs.money_deposit_error_description')">
                    <template #right>
                        <div class="flex items-center">
                            <BaseButtonMuted v-if="getAdminEmail()" :href="`mailto:${getAdminEmail()}`">
                                {{ $t('contact_support') }}
                            </BaseButtonMuted>
                        </div>
                    </template>
                </InlineAlert>
            </section>
            <section aria-labelledby="pending-withdrawals">
                <WalletPendingWithdrawals />
            </section>

            <div class="grid max-w-3xl grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
                <div class="space-y-6 lg:col-span-2 lg:col-start-1">
                    <section
                        v-if="
                            walletState.wallet ||
                            (isNaturalProfile() && accountState.naturalProfile.vouchers.length > 0)
                        "
                        aria-labelledby="wallet-header">
                        <WalletHeader />
                    </section>

                    <VoucherOverview
                        v-if="isNaturalProfile() && accountState.naturalProfile.vouchers.length > 0"
                        :vouchers="accountState.naturalProfile.vouchers" />

                    <!-- Description list-->
                    <div
                        v-if="!walletState.wallet"
                        class="flex h-full items-center justify-center bg-white text-center shadow sm:rounded-lg">
                        <div class="my-4">
                            <WalletIcon stroke-width="0.8" class="mx-auto h-12 w-12 text-gray-400" />
                            <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('wallet.no_wallet') }}</h3>
                            <p class="mt-1 text-sm text-gray-500" v-html="$t('wallet.no_wallet_description')"></p>
                        </div>
                    </div>
                    <template v-else>
                        <section aria-labelledby="transactions">
                            <WalletTransactions />
                        </section>
                    </template>
                </div>

                <section aria-labelledby="user-profile" class="lg:col-span-1 lg:col-start-3">
                    <AccountSidePanel />
                </section>
            </div>
        </main>
        <div v-else class="flex h-[65vh] items-center justify-center">
            <LoadingSpinner class="h-10 w-10 fill-primary" />
        </div>
    </div>
</template>

<style scoped></style>
